import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  Loader2, Send, RotateCcw, Pencil, Trash2, Calendar as CalIcon, CheckCircle2, XCircle,
  PalmtreeIcon,
} from "lucide-react";
import { formatMinutes, formatDecimalHours, formatTimeInTz, formatWeekLabel } from "@/lib/timeFormat";
import BreakViolationIcon from "@/components/timesheet/BreakViolationIcon";
import WeeklyBreakdown from "@/components/timesheet/WeeklyBreakdown";
import RequestPtoDialog from "@/components/pto/RequestPtoDialog";
import { useRuntimeEngines } from "@/lib/runtimeEngines";

export default function WeekPage() {
  const [ts, setTs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitOpen, setSubmitOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [params] = useSearchParams();
  const periodParam = params.get("period_start_local") || params.get("period");
  const [ptoOpen, setPtoOpen] = useState(false);
  const [ptoTypes, setPtoTypes] = useState([]);
  const engines = useRuntimeEngines();

  const load = async () => {
    setLoading(true);
    try {
      const qs = periodParam ? `?period_start_local=${periodParam}` : "";
      const { data } = await api.get(`/time/my-timesheet${qs}`);
      setTs(data);
    } catch (_e) {}
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [periodParam]);

  useEffect(() => {
    if (!engines.pto) return;
    let cancelled = false;
    api.get("/pto/types").then((r) => {
      if (!cancelled) setPtoTypes(r.data?.items || []);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [engines.pto]);

  const doSubmit = async () => {
    setBusy(true);
    try {
      await api.post("/time/my-timesheet/submit", { period_start_local: ts.period_start_local });
      toast.success("Timesheet sent to your approver.");
      setSubmitOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };

  const doResubmit = async () => {
    setBusy(true);
    try {
      await api.post("/time/my-timesheet/resubmit", { period_start_local: ts.period_start_local });
      toast.success("Resent to your approver.");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };

  const removeEntry = async (id) => {
    try {
      await api.delete(`/time/entries/${id}`);
      toast.success("Entry deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (loading || !ts) {
    return <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>;
  }

  const editable = ts.status === "draft" || ts.status === "rejected";
  const isSubmitted = ts.status === "submitted";
  const isApproved = ts.status === "approved";
  const isRejected = ts.status === "rejected";

  return (
    <div className="space-y-6" data-testid="week-page">
      <div>
        <div className="text-xs font-medium uppercase tracking-widest text-muted-foreground">My time</div>
        <h1 className="mt-1 font-display text-3xl font-semibold text-primary">This week</h1>
        <p className="mt-2 text-muted-foreground">
          <CalIcon className="mr-1 inline h-4 w-4" />
          {formatWeekLabel(ts.period_start_local, ts.period_end_local)} · <span className="font-mono text-xs">{ts.tenant_timezone}</span>
        </p>
      </div>

      {engines.pto && (
        <div className="flex justify-end">
          <Button
            variant="outline"
            onClick={() => setPtoOpen(true)}
            disabled={ptoTypes.length === 0}
            data-testid="request-pto-btn"
          >
            <PalmtreeIcon className="h-4 w-4" /> Request time off
          </Button>
        </div>
      )}

      <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-5">
        <SummaryCard label="Total hours" value={`${formatMinutes(ts.total_minutes)} (${formatDecimalHours(ts.total_minutes)}h)`} testid="week-total" />
        <SummaryCard label="REG" value={formatMinutes(ts.pay_summary?.totals?.reg_minutes || 0)} testid="week-reg" tone="primary" />
        {/* Phase 27b — only show the OT/DT tiles when there's something to see.
            Plain weeks (both zero) collapse to REG + PTO + Total to reduce noise. */}
        {((ts.pay_summary?.totals?.ot_minutes || 0) > 0
          || (ts.pay_summary?.totals?.dt_minutes || 0) > 0) && (
          <>
            <SummaryCard label="OT" value={formatMinutes(ts.pay_summary?.totals?.ot_minutes || 0)} testid="week-ot" tone="amber" />
            <SummaryCard label="DT" value={formatMinutes(ts.pay_summary?.totals?.dt_minutes || 0)} testid="week-dt" tone="destructive" />
          </>
        )}
        <SummaryCard label="PTO" value={formatMinutes(ts.pay_summary?.totals?.pto_minutes || 0)} testid="week-pto" tone="secondary" />
      </div>

      {engines.ot && (
        <WeeklyBreakdown paySummary={ts.pay_summary} testid="week-breakdown" />
      )}

      {isRejected && (
        <Alert variant="destructive" className="border-destructive/40 bg-destructive/5" data-testid="rejected-banner">
          <XCircle className="h-4 w-4" />
          <AlertDescription className="ml-6">
            <div className="font-semibold text-destructive">Timesheet rejected</div>
            <div className="mt-1 text-sm">
              <span className="text-muted-foreground">Reason: </span>
              <span className="italic">{ts.rejection_reason || "No reason provided"}</span>
            </div>
            <div className="mt-3">
              <Button size="sm" onClick={doResubmit} disabled={busy || ts.any_open || ts.entries_count === 0} data-testid="resubmit-btn">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />} Resubmit
              </Button>
            </div>
          </AlertDescription>
        </Alert>
      )}

      {isSubmitted && (
        <Alert data-testid="submitted-banner">
          <AlertDescription className="ml-6">
            Sent to your approver{ts.submitted_at ? ` on ${new Date(ts.submitted_at).toLocaleString()}` : ""}. We'll ping you when they act.
          </AlertDescription>
        </Alert>
      )}

      {isApproved && (
        <Alert className="border-success/40 bg-success/5" data-testid="approved-banner">
          <CheckCircle2 className="h-4 w-4 text-success" />
          <AlertDescription className="ml-6">
            <span className="font-semibold text-success">Approved</span>
            {ts.approved_at ? ` on ${new Date(ts.approved_at).toLocaleString()}` : ""}.
          </AlertDescription>
        </Alert>
      )}

      {ts.status === "draft" && (
        <div className="flex justify-end">
          <Button
            onClick={() => setSubmitOpen(true)}
            disabled={ts.any_open || ts.entries_count === 0}
            data-testid="submit-week-btn"
          >
            <Send className="h-4 w-4" /> Send for approval
          </Button>
        </div>
      )}

      <div className="space-y-3">
        {ts.days.map((day) => {
          const dayPay = ts.pay_summary?.by_day?.[day.date] || { reg: 0, ot: 0, dt: 0, pto: 0 };
          const hasPay = (dayPay.reg + dayPay.ot + dayPay.dt + dayPay.pto) > 0;
          return (
          <Card key={day.date} className={day.entries.length === 0 ? "opacity-60" : ""}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 py-4">
              <CardTitle className="text-base font-medium">
                {new Date(day.date + "T00:00:00").toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric" })}
              </CardTitle>
              <div className="flex items-center gap-3">
                {hasPay && (
                  <div className="flex flex-wrap items-center gap-1" data-testid={`day-pills-${day.date}`}>
                    {dayPay.reg > 0 && <Badge className="rounded-full bg-primary/15 text-primary hover:bg-primary/20 text-[10px]">REG {formatMinutes(dayPay.reg)}</Badge>}
                    {dayPay.ot > 0 && <Badge className="rounded-full bg-amber-500/15 text-amber-700 hover:bg-amber-500/20 text-[10px]">OT {formatMinutes(dayPay.ot)}</Badge>}
                    {dayPay.dt > 0 && <Badge className="rounded-full bg-destructive/15 text-destructive hover:bg-destructive/20 text-[10px]">DT {formatMinutes(dayPay.dt)}</Badge>}
                    {dayPay.pto > 0 && <Badge className="rounded-full bg-secondary/15 text-secondary hover:bg-secondary/20 text-[10px]">PTO {formatMinutes(dayPay.pto)}</Badge>}
                  </div>
                )}
                <div className="text-sm text-muted-foreground">
                  {day.entries.length === 0 ? "No punches" : `${formatMinutes(day.total_minutes)} (${formatDecimalHours(day.total_minutes)}h)`}
                </div>
              </div>
            </CardHeader>
            {day.entries.length > 0 && (
              <CardContent className="space-y-2 pt-0">
                {day.entries.map((e) => (
                  <div key={e.id} className="flex items-center justify-between gap-3 rounded-lg border bg-accent/30 p-3 text-sm" data-testid={`entry-row-${e.id}`}>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <div className="font-medium">
                          {e.source === "pto"
                            ? <span className="italic">PTO · {formatMinutes(e.duration_minutes)}</span>
                            : e.entry_mode === "duration"
                            ? <span>Hours entered · {formatMinutes(e.duration_minutes)}</span>
                            : (<>
                                {formatTimeInTz(e.clock_in_at, ts.tenant_timezone)} → {e.clock_out_at ? formatTimeInTz(e.clock_out_at, ts.tenant_timezone) : <span className="text-secondary">still open</span>}
                                {e.duration_minutes != null && (
                                  <span className="ml-2 text-muted-foreground">· {formatMinutes(e.duration_minutes)}</span>
                                )}
                              </>)}
                        </div>
                        {engines.break && e.source !== "pto" && (
                          <BreakViolationIcon
                            entry={e}
                            role="worker"
                            onNoteSaved={load}
                          />
                        )}
                      </div>
                      {e.source !== "pto" && (e.raw_minutes != null && (e.unpaid_break_minutes + e.auto_deducted_minutes) > 0) && (
                        <div className="mt-1 text-xs text-muted-foreground font-mono" data-testid={`entry-break-line-${e.id}`}>
                          gross {formatMinutes(e.raw_minutes)} − {formatMinutes(e.unpaid_break_minutes + e.auto_deducted_minutes)} break{e.auto_deducted_minutes > 0 ? " (auto-deduct)" : ""} = net {formatMinutes(e.duration_minutes || 0)}
                        </div>
                      )}
                      <div className="mt-1 flex flex-wrap items-center gap-1">
                        <Badge variant="secondary" className="rounded-full text-[10px] uppercase">{e.source}</Badge>
                        {(e.dimension_values_resolved || []).map((dv) => (
                          <Badge key={dv.key} variant="outline" className="rounded-full">{dv.key}: {dv.code}</Badge>
                        ))}
                        {(e.checklist_required_total > 0) && (
                          <Badge variant="outline" className={`rounded-full text-[10px] ${e.checklist_required_unchecked === 0 ? "border-success text-success" : "border-amber-500 text-amber-700"}`}>
                            ✓ {e.checklist_required_checked}/{e.checklist_required_total} required
                          </Badge>
                        )}
                      </div>
                      {e.notes && <div className="mt-1 text-muted-foreground">{e.notes}</div>}
                    </div>
                    {editable && e.status === "closed" && e.source !== "pto" && (
                      <div className="flex gap-1">
                        <Button size="icon" variant="ghost" onClick={() => setEditing(e)} data-testid={`edit-entry-${e.id}`}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button size="icon" variant="ghost" onClick={() => removeEntry(e.id)} data-testid={`delete-entry-${e.id}`}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </CardContent>
            )}
          </Card>
        );})}
      </div>

      <SubmitConfirmDialog
        open={submitOpen}
        onClose={() => setSubmitOpen(false)}
        ts={ts}
        onConfirm={doSubmit}
        busy={busy}
      />

      <EditEntryDialog
        entry={editing}
        tz={ts.tenant_timezone}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); load(); }}
      />

      {engines.pto && (
        <RequestPtoDialog
          open={ptoOpen}
          onClose={() => setPtoOpen(false)}
          onSubmitted={() => { setPtoOpen(false); load(); }}
          types={ptoTypes}
        />
      )}
    </div>
  );
}

function SummaryCard({ label, value, testid, tone = "primary" }) {
  const toneClass = {
    primary: "text-primary",
    amber: "text-amber-600",
    destructive: "text-destructive",
    secondary: "text-secondary",
  }[tone] || "text-primary";
  return (
    <Card className="card-elevate" data-testid={testid}>
      <CardContent className="pt-6">
        <div className="text-xs uppercase tracking-widest text-muted-foreground">{label}</div>
        <div className={`mt-2 font-display text-2xl font-semibold ${toneClass}`}>{value}</div>
      </CardContent>
    </Card>
  );
}

function SubmitConfirmDialog({ open, onClose, ts, onConfirm, busy }) {
  if (!ts) return null;
  const daysWorked = ts.days.filter((d) => d.total_minutes > 0).length;
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="submit-dialog">
        <DialogHeader><DialogTitle>Send this week for approval?</DialogTitle></DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="rounded-lg border bg-accent/40 p-4">
            <div className="text-muted-foreground">Week</div>
            <div className="font-medium">{formatWeekLabel(ts.period_start_local, ts.period_end_local)}</div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="rounded-lg border p-3">
              <div className="text-xs uppercase text-muted-foreground">Total</div>
              <div className="font-display text-lg">{formatMinutes(ts.total_minutes)}</div>
            </div>
            <div className="rounded-lg border p-3">
              <div className="text-xs uppercase text-muted-foreground">Days</div>
              <div className="font-display text-lg">{daysWorked}</div>
            </div>
            <div className="rounded-lg border p-3">
              <div className="text-xs uppercase text-muted-foreground">Entries</div>
              <div className="font-display text-lg">{ts.entries_count}</div>
            </div>
          </div>
          <p className="text-muted-foreground">
            Once you send this, your entries lock until your approver reviews.
          </p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={onConfirm} disabled={busy} data-testid="submit-confirm-btn">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Send for approval
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditEntryDialog({ entry, tz, onClose, onSaved }) {
  const [ci, setCi] = useState("");
  const [co, setCo] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (entry) {
      setCi(toDatetimeLocal(entry.clock_in_at, tz));
      setCo(toDatetimeLocal(entry.clock_out_at, tz));
      setNotes(entry.notes || "");
      setErr("");
    }
  }, [entry, tz]);

  if (!entry) return null;

  const save = async () => {
    setBusy(true); setErr("");
    try {
      await api.patch(`/time/entries/${entry.id}`, {
        clock_in_at: fromDatetimeLocal(ci, tz),
        clock_out_at: fromDatetimeLocal(co, tz),
        notes,
      });
      toast.success("Entry updated");
      onSaved();
    } catch (e) {
      setErr(formatApiError(e));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!entry} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="edit-entry-dialog">
        <DialogHeader><DialogTitle>Edit entry</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-2">
            <Label>Clock in</Label>
            <Input type="datetime-local" value={ci} onChange={(e) => setCi(e.target.value)} data-testid="edit-ci" />
          </div>
          <div className="space-y-2">
            <Label>Clock out</Label>
            <Input type="datetime-local" value={co} onChange={(e) => setCo(e.target.value)} data-testid="edit-co" />
          </div>
          <div className="space-y-2">
            <Label>Notes</Label>
            <Input value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="edit-notes" />
          </div>
          {err && <Alert variant="destructive"><AlertDescription>{err}</AlertDescription></Alert>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy} data-testid="edit-entry-save">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function toDatetimeLocal(iso, tz) {
  if (!iso) return "";
  try {
    const dt = new Date(iso);
    const fmt = new Intl.DateTimeFormat("en-CA", {
      timeZone: tz || "UTC",
      year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit", hour12: false,
    });
    const parts = Object.fromEntries(fmt.formatToParts(dt).map((p) => [p.type, p.value]));
    return `${parts.year}-${parts.month}-${parts.day}T${parts.hour === "24" ? "00" : parts.hour}:${parts.minute}`;
  } catch { return ""; }
}

function fromDatetimeLocal(local, tz) {
  if (!local) return null;
  const [datePart, timePart] = local.split("T");
  const [y, m, d] = datePart.split("-").map(Number);
  const [hh, mm] = timePart.split(":").map(Number);
  const asIfUtc = new Date(Date.UTC(y, m - 1, d, hh, mm));
  const tzFmt = new Intl.DateTimeFormat("en-US", {
    timeZone: tz || "UTC",
    hour12: false, timeZoneName: "shortOffset",
  });
  const off = tzFmt.formatToParts(asIfUtc).find((p) => p.type === "timeZoneName")?.value || "GMT+0";
  const match = off.match(/GMT([+-]\d{1,2})(?::(\d{2}))?/);
  const offH = match ? parseInt(match[1], 10) : 0;
  const offM = match && match[2] ? parseInt(match[2], 10) * Math.sign(offH || 1) : 0;
  const utc = new Date(asIfUtc.getTime() - (offH * 60 + offM) * 60_000);
  return utc.toISOString();
}
